export type SourceProductIdentity = {
  sku: string | null;
  color_code?: string | null;
  size?: string | null;
  barcode?: string | null;
  product_name?: string | null;
};

export type ApprovedProductMapping = {
  source_product_code: string | null;
  source_color_code: string | null;
  source_size_code: string | null;
  source_barcode: string | null;
  product_variant_id: string | null;
  product_id?: string | null;
  source_product_name?: string | null;
  status?: string;
};

const clean = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

function normalizeApprovedProductName(value: string | null | undefined): string {
  return (value ?? "").toLocaleLowerCase("tr-TR")
    .replace(/[ıİ]/g, "i")
    .normalize("NFD").replace(/[\\u0300-\\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ").trim().replace(/\\s+/g, " ");
}

/**
 * Builds a SKU-indexed resolver so dashboard reconciliation does not scan the
 * entire mapping table for every sales and stock row.
 */
export function createApprovedVariantResolver(mappings: ApprovedProductMapping[]) {
  const bySku = new Map<string, ApprovedProductMapping[]>();
  for (const mapping of mappings) {
    const sku = clean(mapping.source_product_code);
    if (!sku) continue;
    const bucket = bySku.get(sku) ?? [];
    bucket.push(mapping);
    bySku.set(sku, bucket);
  }

  return (source: SourceProductIdentity): string | null => {
    const sku = clean(source.sku);
    if (!sku) return null;
    const color = clean(source.color_code);
    const size = clean(source.size);
    const barcode = clean(source.barcode);

    // A SKU alone identifies a product family, not necessarily its variant.
    if (!barcode && !(color && size)) return null;

    const candidates = bySku.get(sku) ?? [];
    const targetIds = new Set<string>();
    for (const mapping of candidates) {
      if (mapping.status && mapping.status !== "approved") continue;
      if (!mapping.product_variant_id) continue;
      // Every supplied identity attribute must match a populated mapping field.
      if (color && clean(mapping.source_color_code) !== color) continue;
      if (size && clean(mapping.source_size_code) !== size) continue;
      if (barcode && clean(mapping.source_barcode) !== barcode) continue;
      targetIds.add(mapping.product_variant_id);
      if (targetIds.size > 1) return null;
    }
    return targetIds.size === 1 ? targetIds.values().next().value ?? null : null;
  };
}

export function resolveApprovedVariant(
  source: SourceProductIdentity,
  mappings: ApprovedProductMapping[],
): string | null {
  return createApprovedVariantResolver(mappings)(source);
}

/** Returns costs only when every row for a canonical key has the same positive finite cost. */
export function buildUnambiguousUnitCosts<T>(
  rows: T[],
  keyOf: (row: T) => string | null,
  costOf: (row: T) => number | null,
): Map<string, number> {
  const candidates = new Map<string, Set<number>>();
  const invalidKeys = new Set<string>();
  for (const row of rows) {
    const key = keyOf(row);
    if (!key) continue;
    const cost = costOf(row);
    if (cost == null || !Number.isFinite(cost) || cost <= 0) {
      invalidKeys.add(key);
      continue;
    }
    const set = candidates.get(key) ?? new Set<number>();
    set.add(cost);
    candidates.set(key, set);
  }
  const result = new Map<string, number>();
  for (const [key, costs] of candidates) {
    if (!invalidKeys.has(key) && costs.size === 1) result.set(key, costs.values().next().value as number);
  }
  return result;
}


export type InvoiceCostPosting = {
  product_id: string | null;
  variant_id: string | null;
  quantity: number | string | null;
  unit_cost: number | string | null;
};

export type WeightedInvoiceCosts = {
  byVariant: Map<string, number>;
  byProduct: Map<string, number>;
};

/**
 * Calculate purchase unit costs from real supplier invoice lines, including
 * drafts used for analytical costing before stock/cari finalization.
 * Any malformed row invalidates its product/variant aggregate so a partial
 * weighted average can never masquerade as a complete cost.
 */
export function buildWeightedAverageInvoiceCosts(postings: InvoiceCostPosting[]): WeightedInvoiceCosts {
  type Aggregate = { quantity: number; totalCost: number };
  const products = new Map<string, Aggregate>();
  const variants = new Map<string, Aggregate>();
  const invalidProducts = new Set<string>();
  const invalidVariants = new Set<string>();

  for (const posting of postings) {
    const productId = posting.product_id?.trim() || null;
    const variantId = posting.variant_id?.trim() || null;
    const quantity = Number(posting.quantity);
    const unitCost = Number(posting.unit_cost);
    const valid = Number.isFinite(quantity) && quantity > 0 && Number.isFinite(unitCost) && unitCost > 0;

    if (productId) {
      if (!valid) {
        invalidProducts.add(productId);
      } else {
        const aggregate = products.get(productId) ?? { quantity: 0, totalCost: 0 };
        aggregate.quantity += quantity;
        aggregate.totalCost += quantity * unitCost;
        products.set(productId, aggregate);
      }
    }
    if (variantId) {
      if (!valid) {
        invalidVariants.add(variantId);
      } else {
        const aggregate = variants.get(variantId) ?? { quantity: 0, totalCost: 0 };
        aggregate.quantity += quantity;
        aggregate.totalCost += quantity * unitCost;
        variants.set(variantId, aggregate);
      }
    }
  }

  const average = (items: Map<string, Aggregate>, invalid: Set<string>) => {
    const result = new Map<string, number>();
    for (const [id, aggregate] of items) {
      if (invalid.has(id) || aggregate.quantity <= 0) continue;
      const value = aggregate.totalCost / aggregate.quantity;
      if (Number.isFinite(value) && value > 0) result.set(id, value);
    }
    return result;
  };

  return {
    byVariant: average(variants, invalidVariants),
    byProduct: average(products, invalidProducts),
  };
}


/**
 * Resolves the product family from an approved exact Nebim SKU mapping.
 * A SKU may represent a product family rather than a size/color variant,
 * so this returns a product only when every approved row for that SKU points
 * to the same product_id. Ambiguous or incomplete mappings fail closed.
 */
export function createApprovedProductResolver(mappings: ApprovedProductMapping[]) {
  const bySku = new Map<string, Set<string>>();
  for (const mapping of mappings) {
    if (mapping.status && mapping.status !== "approved") continue;
    const sku = clean(mapping.source_product_code);
    const productId = mapping.product_id?.trim();
    if (!sku || !productId) continue;
    const products = bySku.get(sku) ?? new Set<string>();
    products.add(productId);
    bySku.set(sku, products);
  }

  const byName = new Map<string, Set<string>>();
  for (const mapping of mappings) {
    if (mapping.status && mapping.status !== "approved") continue;
    const name = normalizeApprovedProductName(mapping.source_product_name);
    const productId = mapping.product_id?.trim();
    if (!name || !productId) continue;
    const products = byName.get(name) ?? new Set<string>();
    products.add(productId);
    byName.set(name, products);
  }

  return (source: SourceProductIdentity): string | null => {
    const sku = clean(source.sku);
    if (sku) {
      const products = bySku.get(sku);
      return products?.size === 1 ? products.values().next().value ?? null : null;
    }
    // Name-only imports resolve only against an explicitly approved, unique
    // source description. Similarity suggestions remain in the review queue.
    const name = normalizeApprovedProductName(source.product_name);
    const products = name ? byName.get(name) : undefined;
    return products?.size === 1 ? products.values().next().value ?? null : null;
  };
}


const CONSUMABLE_INVOICE_NUMBERS = new Set(["BSU2026000003736", "EKO2026000000043"]);
const DESCRIPTION_NOISE = new Set(["siyah","beyaz","kirmizi","kırmızı","lacivert","mavi","yesil","yeşil","gri","sari","sarı","turuncu","mor","bej","bordo","black","white","red","blue","navy","green","grey","gray","xs","s","m","l","xl","xxl","xxxl","2xl","3xl","4xl","5xl","small","medium","large","extra"]);
const normalizeProductText = (value: string | null | undefined) => (value ?? "")
  .toLocaleLowerCase("tr-TR")
  .replace(/[ıİ]/g, "i").replace(/[çÇ]/g, "c").replace(/[ğĞ]/g, "g")
  .replace(/[öÖ]/g, "o").replace(/[şŞ]/g, "s").replace(/[üÜ]/g, "u")
  .replace(/[^a-z0-9]+/g, " ")
  .trim().split(" ").filter((token) => token.length > 1 && !DESCRIPTION_NOISE.has(token));

export function isConsumableInvoiceNumber(invoiceNo: string | null | undefined): boolean {
  return CONSUMABLE_INVOICE_NUMBERS.has((invoiceNo ?? "").trim().toLocaleUpperCase("tr-TR"));
}

/** Explicit service rule: custom jersey printing is treated as direct profit, not merchandise COGS. */
export function isDirectProfitService(sku: string | null | undefined, productName?: string | null): boolean {
  const code = (sku ?? "").trim().toLocaleUpperCase("tr-TR");
  if (code === "GNLV5U910004") return true;
  const name = normalizeProductText(productName);
  return name.includes("forma") && name.includes("baskisi") && name.includes("bedeli");
}

export type InvoiceCatalogProduct = { id: string; product_code: string | null; product_name: string | null };

/** Exact code first; otherwise a conservative description match which ignores colour/size words. */
export function createInvoiceProductResolver(products: InvoiceCatalogProduct[]) {
  const byCode = new Map<string, InvoiceCatalogProduct[]>();
  const descriptions = products.map((product) => ({ product, tokens: new Set(normalizeProductText(product.product_name)) }))
    .filter((item) => item.tokens.size > 0);
  for (const product of products) {
    const code = (product.product_code ?? "").trim().toLocaleLowerCase("tr-TR");
    if (!code) continue;
    const list = byCode.get(code) ?? [];
    list.push(product);
    byCode.set(code, list);
  }
  return (sourceCode: string | null | undefined, sourceDescription: string | null | undefined): string | null => {
    const code = (sourceCode ?? "").trim().toLocaleLowerCase("tr-TR");
    const exact = byCode.get(code) ?? [];
    if (exact.length === 1) return exact[0].id;
    if (exact.length > 1) return null;
    const sourceTokens = new Set(normalizeProductText(sourceDescription));
    if (sourceTokens.size < 2) return null;
    const scored = descriptions.map(({ product, tokens }) => {
      const overlap = [...sourceTokens].filter((token) => tokens.has(token)).length;
      const score = (2 * overlap) / (sourceTokens.size + tokens.size);
      return { id: product.id, score };
    }).filter((item) => item.score >= 0.82).sort((a, b) => b.score - a.score);
    if (!scored.length || (scored[1] && scored[0].score - scored[1].score < 0.12)) return null;
    return scored[0].id;
  };
}


/** Variant purchase cost takes precedence; product-level cost is a safe fallback. */
export function resolveVariantFirstUnitCost(
  variantId: string | null | undefined,
  productId: string | null | undefined,
  costs: ReadonlyMap<string, number>,
  fallbackKey?: string | null,
): number | undefined {
  const variantCost = variantId ? costs.get("variant:" + variantId) : undefined;
  if (variantCost != null && Number.isFinite(variantCost) && variantCost > 0) return variantCost;
  const productCost = productId ? costs.get("product:" + productId) : undefined;
  if (productCost != null && Number.isFinite(productCost) && productCost > 0) return productCost;
  const fallbackCost = fallbackKey ? costs.get(fallbackKey) : undefined;
  return fallbackCost != null && Number.isFinite(fallbackCost) && fallbackCost > 0 ? fallbackCost : undefined;
}
